import React, { useState } from "react";
import {
  withStyles,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Box,
  Chip,
  Button,
  Grid,
  IconButton,
  Popover,
  Divider,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  createTag,
  deleteTag,
  updateObjetivos,
  updateTag,
} from "../../../../services/tags";
import EditIcon from "@mui/icons-material/Edit";
import { Delete } from "@material-ui/icons";
import { useListadoEtiquetas } from "../../../../hooks/useListadoEtiquetas";
import ChipTag from "../../../../components/TagsAtentus/ChipTag";
import compose from "recompose/compose";
import { withRouter } from "react-router-dom";
import { withTranslation } from 'react-i18next';
import { getQueryParams } from "../../../../utils/fns/get-query-params";
import { toast } from "react-toastify";

function getAuthData() {
  const cliente_id = sessionStorage.getItem("client_id");
  const user_id = sessionStorage.getItem("user");
  return { cliente_id, user_id };
}

const randomColors = [
  "#7eab71",
  "#86e36b",
  "#49b3af",
  "#405ea3",
  "#8233a1",
  "#a6337c",
  "#bf632e",
  "#b09c28",
];

const getRandomColor = () => {
  const random = getRandomInt(randomColors.length - 1);
  return randomColors[random];
};

function getRandomInt(max) {
  return Math.floor(Math.random() * max);
}

const styles = {
  listItem: {
    padding: 5,
    "&:hover": {
      backgroundColor: "#e6e6e6",
    },
  },
  customChip: {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    color: "white",
    "&:hover": {
      cursor: "pointer",
    },
    borderRadius: 5,
    maxWidth: "100%",
  },
  inputTag: {
    width: "100%",
  },
  deleteContainer: {
    color: "#666666",
    padding: 2,
    borderRadius: 5,
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#e8e8e8",
    },
  },
};

const TagList = ({
  tags,
  filteredTags,
  classes,
  refetch,
  t,
}) => {
  const checkTagNameExist = ({ tagName, id }) => {
    const exist = tags.find((item) => tagName.trim() === item.nombre.trim());
    return exist && exist.etiqueta_id !== id;
  };

  const updateDataTag = async ({ etiqueta_id, nombre, color }) => {
    try {
      const { cliente_id, user_id } = getAuthData();
      const response = await updateTag({
        etiqueta_id,
        color,
        nombre,
        descripcion: "",
        usuario_creacion_id: parseInt(user_id),
        cliente_id: parseInt(cliente_id),
      });
      if (response.status === 200) {
        toast.success(t('tags.toast.updated'));
        refetch();
        return;
      }
      throw new Error("Error al actualizar la etiqueta");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message ?? t('tags.toast.updateError')
      );
    }
  };

  const deleteDataTag = async ({ etiqueta_id }) => {
    try {
      const { user_id } = getAuthData();
      const response = await deleteTag({
        etiqueta_id,
        usuario_modificacion: parseInt(user_id),
      });
      if (response.status === 200) {
        toast.success(t('tags.toast.deleted'));
        refetch();
        return;
      }
      throw new Error("Error al eliminar la etiqueta");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message ?? t('tags.toast.deleteError')
      );
    }
  };

  return (
    <>
      {filteredTags.map((tag) => (
        <TagEditable
          key={tag.etiqueta_id}
          tag={tag}
          classes={classes}
          checkTagNameExist={checkTagNameExist}
          updateDataTag={(params) => updateDataTag(params)}
          deleteDataTag={(params) => deleteDataTag(params)}
          t={t}
        />
      ))}
    </>
  );
};

const TagEditable = ({
  tag,
  classes,
  checkTagNameExist,
  updateDataTag,
  deleteDataTag,
  t,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tagInputText, setTagInputText] = useState(tag.nombre);
  const [color, setColor] = useState(tag.color);

  const disableButton = checkTagNameExist({
    tagName: tagInputText,
    id: tag.etiqueta_id,
  });

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeTagInputText = (event) => {
    const value = event.target.value;
    setTagInputText(value);
  };

  const handleUpdateTags = () => {
    updateDataTag({
      color,
      nombre: tagInputText,
      etiqueta_id: tag.etiqueta_id,
    });
  };

  return (
    <div className={classes.listItem}>
      <Grid container>
        <Grid item xs={11}>
          <Box display='flex' alignItems='center' style={{height: '100%'}}>
            <ChipTag nombre={tag.nombre} color={tag.color} size="medium" />
          </Box>
        </Grid>
        <Grid item xs={1} style={{ display: "flex", justifyContent: "center" }}>
          <IconButton size="small" onClick={handleClick}>
            <EditIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box style={{ maxWidth: 350 }}>
          <Box padding={1}>
            <Box
              display="flex"
              className={classes.deleteContainer}
              alignItems="center"
              onClick={() => deleteDataTag({ etiqueta_id: tag.etiqueta_id })}
            >
              <Delete fontSize="small" />
              <span style={{ marginLeft: 5, fontSize: "15px" }}>{t('tags.popover.delete')}</span>
            </Box>
          </Box>
          <Divider />
          <Box padding={1}>
            <Box>
              <TextField
                value={tagInputText}
                size="small"
                variant="outlined"
                onChange={handleChangeTagInputText}
                style={{ width: "100%" }}
                error={disableButton}
              />
            </Box>
            {disableButton && (
              <Box paddingTop={1} paddingBottom={1}>
                <Typography variant="body2" color="error">
                  {t('tags.validation.exists')}
                </Typography>
              </Box>
            )}
            <Box
              marginTop={1}
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Chip
                label={tagInputText}
                style={{
                  backgroundColor: color,
                  color: "white",
                  maxWidth: "85%",
                }}
                className={classes.customChip}
              />
              <input
                type="color"
                onChange={(e) => setColor(e.target.value)}
                value={color}
                style={{ marginLeft: 10 }}
              />
            </Box>
            <Box marginTop={1}>
              <Button
                fullWidth
                size="small"
                disabled={disableButton || tagInputText === ""}
                onClick={() => handleUpdateTags()}
              >
                {t('tags.save')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

const Tags = ({ classes, history, t }) => {
  const [tagInputText, setTagInputText] = useState("");
  const randomColor = getRandomColor();
  const [color, setColor] = useState(randomColor);
  const {
    data: allTags,
    isLoading: isLoadingTags,
    refetch,
  } = useListadoEtiquetas();

  const tags = allTags.length ? allTags.filter((tag) => tag.activo) : [];

  const filteredTags = () => {
    return tags.filter((tag) => {
      return tag.nombre.toLowerCase().match(tagInputText.toLowerCase());
    });
  };

  const handleChangeTagInputText = (event) => {
    const value = event.target.value;
    setTagInputText(value);
  };

  const updateList = async () => {
    await updateObjetivos();
    refetch({});
    history.push({ pathname: "manageObjectives", search: getQueryParams(), query: { tab: 5 } });
  };

  const handleClickCreateTag = async () => {
    try {
      const { cliente_id, user_id } = getAuthData();
      const response = await createTag({
        color,
        nombre: tagInputText,
        descripcion: "",
        usuario_creacion_id: parseInt(user_id),
        cliente_id: parseInt(cliente_id),
      });
      if (response.status === 200) {        
        toast.success(t('tags.toast.created'));
        refetch({});
        setTagInputText("");
        return;
      }
      throw new Error("Error al crear la etiqueta");
    } catch (err) {
      toast.error(
        err?.message ?? t('tags.toast.createError')
      );
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('tags.title')}
        titleTypographyProps={{
          style: {
            fontSize: "22px",
          },
        }}
        subheader={t('tags.subheader')}
      />
      <CardContent>
        <Grid container>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <div>
                <TextField
                variant="outlined"
                size="small"
                onChange={handleChangeTagInputText}
                value={tagInputText}
                label={t('tags.inputLabel')}
                className={classes.inputTag}
                disabled={isLoadingTags}
              />
            </div>
            {tagInputText !== "" &&
            filteredTags().length === 0 &&
            !isLoadingTags ? (
              <Box marginTop={2} display="flex" style={{ padding: 5 }}>
                <ChipTag nombre={tagInputText} color={color} size="medium" />
                <input
                  type="color"
                  onChange={(e) => setColor(e.target.value)}
                  value={color}
                  style={{ marginLeft: 10 }}
                />
                <Button
                  style={{ marginLeft: 10 }}
                  onClick={handleClickCreateTag}
                >
                  {t('tags.create')}
                </Button>
              </Box>
            ) : null}
            <Box marginTop={2}>
              {isLoadingTags && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress
                    variant="indeterminate"
                    size={12}
                    thickness={3}
                    style={{ marginRight: 10 }}
                  />
                  <Typography variant="body1">
                    {t('tags.loadingList')}
                  </Typography>
                </div>
              )}
              <TagList
                tags={tags}
                filteredTags={filteredTags()}
                classes={classes}
                refetch={() => updateList()}
                t={t}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default compose(withRouter, withStyles(styles), withTranslation())(Tags);
